import React, { useEffect, useMemo, useState } from "react";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import axios from "../../axios";
import SamplePreviewModal from "./SamplePreviewModal";
import WarrantyTermsModal from "./WarrantyTermsModal";

function resolvePublicRelativeUrl(path) {
  const raw = String(path || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  const normalized = raw.replace(/^\/+/, "");
  return `/${normalized}`;
}

export default function RangeComparisonModal({ isOpen, toggle }) {
  const [ranges, setRanges] = useState(null);
  const [selectedRange, setSelectedRange] = useState("eco");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [samplePreview, setSamplePreview] = useState({
    isOpen: false,
    name: "",
    url: "",
  });
  const [termsPreview, setTermsPreview] = useState({
    isOpen: false,
    title: "",
    terms: null,
  });

  useEffect(() => {
    if (!isOpen) return;
    let mounted = true;
    setLoading(true);
    setError("");
    axios
      .get("/store/products/range_comparison")
      .then((resp) => {
        if (!mounted) return;
        setRanges(resp.data.ranges || null);
      })
      .catch((err) => {
        if (!mounted) return;
        setError(
          err?.response?.data?.error?.message ||
            err?.response?.data?.error ||
            err?.message ||
            "Unable to load range comparison."
        );
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedRange("eco");
  }, [isOpen]);

  const eco = ranges?.eco;
  const prime = ranges?.prime;
  const gold = ranges?.gold;
  const hasRangeData = Boolean(eco && prime && gold);
  const activeRange =
    selectedRange === "prime"
      ? prime
      : selectedRange === "gold"
        ? gold
        : eco;

  const rows = useMemo(
    () => {
      if (!hasRangeData || !activeRange) return [];
      return [
        {
          label: "PLYWOOD",
          value: activeRange.core_materials,
        },
        {
          label: "HARDWARE",
          value: activeRange.hardwares,
        },
        {
          label: "WARRANTY",
          value: activeRange.warranty,
        },
        {
          label: "FINISH",
          value: activeRange.finish,
        },
        {
          label: "BACKSHEET",
          value: activeRange.backsheet,
        },
        {
          label: "SHELVES",
          value: activeRange.shelves,
        },
      ];
    },
    [hasRangeData, activeRange]
  );

  const openSample = (name, sampleUrl) => {
    const resolvedUrl = resolvePublicRelativeUrl(sampleUrl);
    if (!resolvedUrl) return;
    setSamplePreview({ isOpen: true, name: name || "Sample", url: resolvedUrl });
  };

  const renderPlywoodCell = (items) => {
    const list = Array.isArray(items) ? items : [];
    if (list.length === 0) return null;
    return (
      <div className="d-flex flex-column gap-1">
        {list.map((entry, idx) => {
          const text = String(entry?.value || "").trim();
          const sampleName = String(entry?.sample_name || text || "Sample").trim();
          const sampleUrl = String(entry?.sample_url || "").trim();
          if (!text) return null;
          return (
            <div key={`${text}-${idx}`} className="small">
              <div>{text}</div>
              {sampleUrl ? (
                <a
                  href="#"
                  className="small text-decoration-underline"
                  onClick={(e) => {
                    e.preventDefault();
                    openSample(sampleName, sampleUrl);
                  }}
                >
                  view sample
                </a>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  };

  const renderFinishCell = (finishObj) => {
    const value = String(finishObj?.value || "").trim();
    const sampleUrl = String(finishObj?.sample?.sample_url || "").trim();
    const sampleName = String(finishObj?.sample?.sample_name || value || "Sample").trim();
    if (!value) return null;
    return (
      <div className="small">
        <div>{value}</div>
        {sampleUrl ? (
          <a
            href="#"
            className="small text-decoration-underline"
            onClick={(e) => {
              e.preventDefault();
              openSample(sampleName, sampleUrl);
            }}
          >
            view sample
          </a>
        ) : null}
      </div>
    );
  };

  const renderWarrantyCell = (warrantyObj) => {
    const years = Number(warrantyObj?.years || 0);
    const title = String(
      warrantyObj?.title || (years > 0 ? `${years}-year warranty` : "")
    ).trim();
    const terms = String(warrantyObj?.terms || "").trim();
    if (!title) return null;

    return (
      <div className="small">
        <div>{title.toLowerCase()}</div>
        {terms ? (
          <a
            href="#"
            className="small text-decoration-underline"
            onClick={(e) => {
              e.preventDefault();
              setTermsPreview({
                isOpen: true,
                title,
                terms: terms,
              });
            }}
          >
            view terms
          </a>
        ) : null}
      </div>
    );
  };

  const cellBorderStyle = {
    borderTop: 0,
    borderBottom: "1px solid #d9dee7",
    backgroundColor: "#f2f2f2",
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle}>ZeroBuild Ranges</ModalHeader>
      <ModalBody className="pt-0">
        {loading ? (
          <div className="small text-muted">Loading comparison...</div>
        ) : null}
        {error ? <div className="alert alert-danger py-2 mb-2">{error}</div> : null}
        {hasRangeData ? (
          <>
            <div className="d-flex justify-content-center gap-2 my-2">
              {["eco", "prime", "gold"].map((rangeKey) => {
                const active = selectedRange === rangeKey;
                return (
                  <button
                    key={rangeKey}
                    type="button"
                    className={
                      active
                        ? "store-products-row__range-pill store-products-row__range-pill--active"
                        : "store-products-row__range-pill"
                    }
                    onClick={() => setSelectedRange(rangeKey)}
                  >
                    {rangeKey.toUpperCase()}
                  </button>
                );
              })}
            </div>
            <div
              className="table-responsive"
              style={{ marginLeft: -16, marginRight: -16 }}
            >
              <table
                className="table align-middle mb-2"
                style={{ backgroundColor: "#f2f2f2", fontSize: "0.85rem" }}
              >
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row.label} className={index % 2 === 0 ? "table-light" : ""}>
                    <th
                      scope="row"
                      className="small text-uppercase"
                      style={{ ...cellBorderStyle, width: 100 }}
                    >
                      {row.label}
                    </th>
                    <td style={cellBorderStyle} className="text-left px-1">
                      {row.label === "PLYWOOD"
                        ? renderPlywoodCell(row.value)
                        : row.label === "WARRANTY"
                          ? renderWarrantyCell(row.value)
                          : row.label === "FINISH"
                            ? renderFinishCell(row.value)
                            : row.value}
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          </>
        ) : null}
      </ModalBody>
      <SamplePreviewModal
        isOpen={samplePreview.isOpen}
        name={samplePreview.name}
        url={samplePreview.url}
        onClose={() => setSamplePreview({ isOpen: false, name: "", url: "" })}
      />
      <WarrantyTermsModal
        isOpen={termsPreview.isOpen}
        title={termsPreview.title}
        terms={termsPreview.terms}
        onClose={() => setTermsPreview({ isOpen: false, title: "", terms: null })}
      />
    </Modal>
  );
}
